import React, { useEffect, useRef, useState } from 'react';
import api, { API_URL } from '../../api';
import { useLanguage } from '../../contexts/LanguageContext';
import { useNotification } from '../../contexts/NotificationContext';
import Icon from './Icon';
import './ZoneMonsterPicker.css';

/** Les images DofusDB passent par le site (cache côté serveur) plutôt que d'être chargées chez DofusDB. */
export const monsterImgSrc = (img) => {
    const m = /\/img\/monsters\/(\d+)\.png$/.exec(img || '');
    return m ? `${API_URL}/api/monster-img/${m[1]}.png` : img;
};

/** Vignette du monstre d'une zone (initiale de la zone sans image). */
export const MonsterThumb = ({ monster, zone, size = 40, className = '' }) => {
    const src = monsterImgSrc(monster?.img);
    const [brokenSrc, setBrokenSrc] = useState(null);
    const style = { width: size, height: size };
    if (src && brokenSrc !== src) {
        return <img className={`zm-thumb ${className}`} style={style} src={src} alt={monster.name || zone} loading="lazy" onError={() => setBrokenSrc(src)} />;
    }
    return <span className={`zm-thumb zm-thumb--empty ${className}`} style={{ ...style, fontSize: size * 0.42 }} aria-hidden="true">{(zone || '?').trim().charAt(0).toUpperCase()}</span>;
};

/**
 * Fenêtre de choix du monstre d'une zone : recherche DofusDB, puis enregistrement immédiat.
 * onSaved(zone, monster) reçoit le monstre enregistré ({ monsterId: null } = sans image).
 */
const ZoneMonsterPicker = ({ serverId, zone, current, onClose, onSaved }) => {
    const { t } = useLanguage();
    const { error: showError } = useNotification();
    const [query, setQuery] = useState(current?.name || zone);
    const [results, setResults] = useState(null);
    const [searching, setSearching] = useState(false);
    const [saving, setSaving] = useState(false);
    const inputRef = useRef(null);

    useEffect(() => { inputRef.current?.select(); }, []);

    // Recherche au fil de la saisie (petite pause pour ne pas interroger DofusDB à chaque lettre)
    useEffect(() => {
        const q = query.trim();
        if (q.length < 2) { setResults([]); return undefined; }
        let cancelled = false;
        const id = setTimeout(() => {
            setSearching(true);
            api.get(`/zone-monsters/${serverId}/search`, { params: { q } })
                .then(res => { if (!cancelled) setResults(res.data?.results || []); })
                .catch(err => { if (!cancelled) { setResults([]); showError(err?.response?.data?.error || t('zoneMonster.searchError', 'Recherche impossible')); } })
                .finally(() => { if (!cancelled) setSearching(false); });
        }, 350);
        return () => { cancelled = true; clearTimeout(id); };
    }, [query, serverId, showError, t]);

    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose]);

    const choose = async (monsterId) => {
        setSaving(true);
        try {
            const res = await api.put(`/zone-monsters/${serverId}/${encodeURIComponent(zone)}`, { monsterId });
            onSaved(zone, res.data.monster);
            onClose();
        } catch (err) {
            showError(err?.response?.data?.error || t('zoneMonster.saveError', 'Le monstre n\'a pas pu être enregistré'));
            setSaving(false);
        }
    };

    return (
        <div className="c2-modal-backdrop" role="presentation" onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
            <div className="c2-card c2-modal zm-picker" role="dialog" aria-modal="true" aria-labelledby="zm-picker-title">
                <div className="c2-modal__head">
                    <h2 id="zm-picker-title">{t('zoneMonster.title', 'Monstre de « {zone} »', { zone })}</h2>
                    <button type="button" className="c2-icon-btn" onClick={onClose} aria-label={t('common.close', 'Fermer')}>
                        <Icon name="close" size={20} />
                    </button>
                </div>
                <p className="c2-muted zm-picker__intro">
                    {t('zoneMonster.intro', 'Son image illustre la zone sur la page des enchères. Les données viennent de DofusDB.')}
                </p>
                <input
                    ref={inputRef}
                    type="search"
                    className="c2-input"
                    value={query}
                    onChange={e => setQuery(e.target.value)}
                    placeholder={t('zoneMonster.searchPlaceholder', 'Nom du monstre (ex : Nileza)')}
                    aria-label={t('zoneMonster.searchLabel', 'Rechercher un monstre')}
                />
                <div className="zm-picker__results" aria-busy={searching}>
                    {results === null || searching ? (
                        <div className="c2-loading"><div className="c2-spinner" /></div>
                    ) : results.length === 0 ? (
                        <p className="c2-muted zm-picker__empty">{t('zoneMonster.noResult', 'Aucun monstre trouvé. Essaie un autre nom.')}</p>
                    ) : (
                        <ul className="zm-picker__grid">
                            {results.map(m => (
                                <li key={m.id}>
                                    <button
                                        type="button"
                                        className={`zm-option${current?.monsterId === m.id ? ' is-current' : ''}`}
                                        onClick={() => choose(m.id)}
                                        disabled={saving}
                                    >
                                        <MonsterThumb monster={m} zone={m.name} size={72} />
                                        <span className="zm-option__name">{m.name}</span>
                                        {m.isBoss && <span className="zm-option__boss">Boss</span>}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
                <div className="c2-modal__actions">
                    {current?.monsterId != null && (
                        <button type="button" className="c2-btn c2-btn--ghost" onClick={() => choose(null)} disabled={saving}>
                            <Icon name="trash" size={16} />
                            {t('zoneMonster.remove', 'Retirer l\'image')}
                        </button>
                    )}
                    <button type="button" className="c2-btn c2-btn--ghost" onClick={onClose} disabled={saving}>{t('common.cancel', 'Annuler')}</button>
                </div>
            </div>
        </div>
    );
};

export default ZoneMonsterPicker;
